import { useCallback, useEffect, useState } from "react";

/**
 * SSO corporativo (epico 7) - domínios de e-mail verificados (auto-join) +
 * config de SAML (só no plano Business, tratado como Enterprise).
 */
interface DomainRow {
  domain: string;
  verified: boolean;
  verifiedAt: string | null;
  recordName: string;
  recordValue: string;
}

interface SamlConfig {
  available: boolean;
  enabled: boolean;
  idpEntryPoint: string;
  idpCert: string;
  metadataUrl: string;
  loginUrl: string;
  acsUrl: string;
}

function fmtDate(iso: string | null): string {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("pt-BR", { timeZone: "America/Sao_Paulo" });
  } catch {
    return iso;
  }
}

interface Props {
  /** Sysadmin só - configura a org escolhida no seletor em vez da própria. */
  orgId?: string;
}

export function SsoPanel({ orgId }: Props) {
  const [domains, setDomains] = useState<DomainRow[]>([]);
  const [newDomain, setNewDomain] = useState("");
  const [domainBusy, setDomainBusy] = useState(false);
  const [domainError, setDomainError] = useState<string | null>(null);

  const [saml, setSaml] = useState<SamlConfig | null>(null);
  const [idpEntryPoint, setIdpEntryPoint] = useState("");
  const [idpCert, setIdpCert] = useState("");
  const [samlEnabled, setSamlEnabled] = useState(false);
  const [samlBusy, setSamlBusy] = useState(false);
  const [samlError, setSamlError] = useState<string | null>(null);
  const [samlSaved, setSamlSaved] = useState(false);

  const qs = orgId ? `?orgId=${encodeURIComponent(orgId)}` : "";

  const loadDomains = useCallback(async () => {
    const r = await fetch(`/api/admin/org/domains${qs}`);
    if (r.ok) setDomains((await r.json()).items ?? []);
  }, [qs]);

  const loadSaml = useCallback(async () => {
    const r = await fetch(`/api/admin/sso/saml${qs}`);
    if (r.ok) {
      const j = (await r.json()) as SamlConfig;
      setSaml(j);
      setIdpEntryPoint(j.idpEntryPoint);
      setIdpCert(j.idpCert);
      setSamlEnabled(j.enabled);
    }
  }, [qs]);

  useEffect(() => {
    void loadDomains();
    void loadSaml();
  }, [loadDomains, loadSaml]);

  const addDomain = async () => {
    if (!newDomain.trim()) return;
    setDomainBusy(true);
    setDomainError(null);
    try {
      const r = await fetch(`/api/admin/org/domains${qs}`, {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({ domain: newDomain.trim() }),
      });
      const j = await r.json().catch(() => ({}));
      if (!r.ok) {
        setDomainError(j.error ?? "Falha ao adicionar domínio.");
        return;
      }
      setNewDomain("");
      await loadDomains();
    } finally {
      setDomainBusy(false);
    }
  };

  const verifyDomain = async (domain: string) => {
    setDomainBusy(true);
    setDomainError(null);
    try {
      const r = await fetch(`/api/admin/org/domains/${encodeURIComponent(domain)}/verify${qs}`, { method: "POST" });
      const j = await r.json().catch(() => ({}));
      if (!r.ok) setDomainError(j.error ?? "Falha ao verificar.");
      await loadDomains();
    } finally {
      setDomainBusy(false);
    }
  };

  const removeDomain = async (domain: string) => {
    setDomainBusy(true);
    try {
      await fetch(`/api/admin/org/domains/${encodeURIComponent(domain)}${qs}`, { method: "DELETE" });
      await loadDomains();
    } finally {
      setDomainBusy(false);
    }
  };

  const saveSaml = async () => {
    setSamlBusy(true);
    setSamlError(null);
    setSamlSaved(false);
    try {
      const r = await fetch(`/api/admin/sso/saml${qs}`, {
        method: "PUT",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({ enabled: samlEnabled, idpEntryPoint, idpCert }),
      });
      const j = await r.json().catch(() => ({}));
      if (!r.ok) {
        setSamlError(j.error ?? "Falha ao salvar.");
        return;
      }
      setSamlSaved(true);
      await loadSaml();
    } finally {
      setSamlBusy(false);
    }
  };

  return (
    <>
      <div className="admin-block">
        <h2>Domínios de e-mail verificados</h2>
        <p className="admin-user-meta" style={{ marginBottom: 10 }}>
          Quem fizer login social ou SAML com um e-mail de um domínio verificado entra automaticamente
          nesta organização, sem precisar de convite.
        </p>
        <div style={{ display: "flex", gap: 8, marginBottom: 10, alignItems: "flex-end" }}>
          <label className="field" style={{ flex: "1 1 240px" }}>
            <span>Domínio</span>
            <input
              type="text"
              value={newDomain}
              onChange={(e) => setNewDomain(e.target.value)}
              placeholder="empresa.com.br"
              spellCheck={false}
            />
          </label>
          <button className="btn-approve" disabled={domainBusy} onClick={() => void addDomain()} type="button">
            Adicionar
          </button>
        </div>
        {domainError && <div className="auth-error" style={{ marginBottom: 10 }}>{domainError}</div>}
        {domains.map((d) => (
          <div key={d.domain} className="admin-block" style={{ marginBottom: 8, padding: 10 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
              <strong>{d.domain}</strong>
              <span className={`badge ${d.verified ? "ok" : "warn"}`}>{d.verified ? "verificado" : "pendente"}</span>
              {d.verified && <span className="admin-user-meta">desde {fmtDate(d.verifiedAt)}</span>}
              <div style={{ marginLeft: "auto", display: "flex", gap: 6 }}>
                {!d.verified && (
                  <button className="btn-approve" disabled={domainBusy} onClick={() => void verifyDomain(d.domain)} type="button">
                    Verificar
                  </button>
                )}
                <button className="btn-reject" disabled={domainBusy} onClick={() => void removeDomain(d.domain)} type="button">
                  Remover
                </button>
              </div>
            </div>
            {!d.verified && (
              <p className="admin-user-meta" style={{ marginTop: 6, wordBreak: "break-all" }}>
                Crie um registro TXT <code>{d.recordName}</code> com o valor <code>{d.recordValue}</code>, aguarde a
                propagação e clique em "Verificar".
              </p>
            )}
          </div>
        ))}
      </div>

      <div className="admin-block">
        <h2>SSO corporativo (SAML)</h2>
        {!saml ? (
          <p className="admin-user-meta">Carregando…</p>
        ) : !saml.available ? (
          <p className="admin-user-meta">
            Disponível só no plano Business - vincule a organização a esse plano em "Plano e billing" pra
            habilitar.
          </p>
        ) : (
          <>
            <p className="admin-user-meta" style={{ marginBottom: 10 }}>
              Cadastre a URL de SSO e o certificado do seu provedor de identidade (Okta, Azure AD, Google
              Workspace etc.), e importe a metadata abaixo no lado deles.
            </p>
            <dl className="admin-kv" style={{ marginBottom: 10 }}>
              <div><dt>Metadata (SP)</dt><dd><a href={saml.metadataUrl} target="_blank" rel="noreferrer">{saml.metadataUrl}</a></dd></div>
              <div><dt>URL de login</dt><dd style={{ wordBreak: "break-all" }}>{saml.loginUrl}</dd></div>
              <div><dt>URL de ACS</dt><dd style={{ wordBreak: "break-all" }}>{saml.acsUrl}</dd></div>
            </dl>
            <label className="field" style={{ marginBottom: 10 }}>
              <span>URL de SSO do IdP</span>
              <input type="url" value={idpEntryPoint} onChange={(e) => setIdpEntryPoint(e.target.value)} placeholder="https://idp.empresa.com/sso" />
            </label>
            <label className="field" style={{ marginBottom: 10 }}>
              <span>Certificado do IdP (PEM)</span>
              <textarea
                value={idpCert}
                onChange={(e) => setIdpCert(e.target.value)}
                rows={6}
                style={{ width: "100%", fontFamily: "monospace", fontSize: 12 }}
                placeholder="-----BEGIN CERTIFICATE-----..."
              />
            </label>
            <label style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 10 }}>
              <input type="checkbox" checked={samlEnabled} onChange={(e) => setSamlEnabled(e.target.checked)} />
              <span>Habilitar login via SAML</span>
            </label>
            {samlError && <div className="auth-error" style={{ marginBottom: 10 }}>{samlError}</div>}
            {samlSaved && !samlError && (
              <div className="admin-empty small" style={{ color: "var(--success, #2a9d5c)", marginBottom: 10 }}>
                Configuração salva.
              </div>
            )}
            <button className="btn-approve" disabled={samlBusy} onClick={() => void saveSaml()} type="button">
              Salvar
            </button>
          </>
        )}
      </div>
    </>
  );
}
